/**
 * El movimiento de la sección de WhatsApp, en un solo lugar.
 *
 * POR QUÉ ESTE ARCHIVO EXISTE
 * Animar adentro del panel no es como animar en cualquier otro lado: hay una
 * regla en `pages/Dashboard.css` que apaga `transform` y `animation` en TODO
 * `.dashboard-container` con `!important`, y encima le fuerza a cada elemento
 * una transición de opacidad. Cualquier animación escrita sin saber eso sale
 * quieta, o sale blanda. Ya pasó con el interruptor y con la landing.
 *
 * La excepción vive en `Dashboard.css` (buscar `[data-wa-anima]`) y hay que
 * usarla de a dos partes, que es lo que este módulo empaqueta:
 *
 *   1. El elemento lleva `data-wa-anima`, que le apaga la transición del panel
 *      —cuando anima framer tiene que animar framer solo— y le habilita un
 *      `transform` leído de la variable `--wa-y`.
 *   2. El movimiento vertical se anima como `--wa-y` y NO como `y`. La `y` de
 *      framer termina en un `transform` inline, y un inline sin `!important`
 *      pierde contra el `transform: none !important` del panel. Una custom
 *      property no la bloquea nadie.
 *
 * O sea: `<motion.div {...anima} variants={...}>`. Si algún día se saca la
 * regla de Dashboard.css, esto sigue andando igual.
 */

/** La curva: sale rápido y frena suave. Es la misma que usa la landing. */
export const CURVA = [0.22, 1, 0.36, 1];

/**
 * Cuánto dura cada cosa. Cortas a propósito: esto es un panel de trabajo, no
 * una landing. Una animación que se nota es una animación que estorba cuando
 * la ves cuarenta veces por día.
 */
export const DURACION = {
  entrada: 0.22,
  salida: 0.14,
  // El hilo entero al cambiar de conversación: un toque más que un mensaje
  // suelto, porque es más superficie moviéndose.
  panel: 0.26,
};

/** Lo que hay que ponerle a cualquier elemento animado acá adentro. */
export const anima = { 'data-wa-anima': true };

/**
 * Aparecer subiendo un poco. Es el gesto base de toda la sección: lo usan los
 * mensajes nuevos, el hilo al abrirse y el panel al cambiar de pestaña.
 *
 * `desde` permite invertirlo (un valor negativo entra bajando), que es lo que
 * distingue "llegó un mensaje" de "se fue el panel anterior".
 */
export const aparecer = (desde = 8) => ({
  initial: { opacity: 0, '--wa-y': `${desde}px` },
  animate: { opacity: 1, '--wa-y': '0px' },
  exit: { opacity: 0, '--wa-y': `${desde}px` },
  transition: { duration: DURACION.entrada, ease: CURVA },
});

/**
 * El mismo gesto pero para listas, con un retardo chico según la posición.
 *
 * El retardo está TOPEADO a propósito, y ahí está toda la diferencia entre que
 * esto quede bien o quede como un escáner. Sin tope, una lista de veinte filas
 * tarda casi un segundo en terminar de aparecer y la pantalla se prende de a
 * pedazos —el problema por el que la landing terminó desactivando su componente
 * de entrada—. Con tope, las primeras cuatro filas se separan 35ms entre sí y
 * el resto entra con ellas: alcanza para que se lea como que la lista se
 * acomoda, y termina en 140ms, que es antes de que puedas fijarte.
 *
 * Va tanto en la carga inicial de la lista como en lo que llegue después. La
 * carga inicial era lo que se sentía peor: el esqueleto se cambiaba por las
 * filas de una sola vez, sin ninguna transición, y eso pega como un salto.
 */
export const enFila = (i) => ({
  ...aparecer(6),
  transition: { duration: DURACION.entrada, ease: CURVA, delay: Math.min(i, 4) * 0.035 },
});

/**
 * El feedback de un botón que dispara algo que tarda: mientras espera, late.
 *
 * Es opacidad y no tamaño porque el tamaño mueve a los vecinos, y este botón
 * vive en una barra con el nombre de la persona al lado.
 */
export const latiendo = {
  animate: { opacity: [1, 0.55, 1] },
  transition: { duration: 1.1, repeat: Infinity, ease: 'easeInOut' },
};
